import React, { useState, useMemo } from "react";
import {
    Container,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import { HeaderPrimary } from "../HeaderText/HeaderText.style";
import {
    validatePassword,
    validatePasswordMatch,
} from "../../utils/passwordValidation";

interface NewPasswordFormProps {
    onSubmit: (newPassword: string) => Promise<void>;
    loading: boolean;
    error?: string;
}

export default function NewPasswordForm({
    onSubmit,
    loading,
    error,
}: NewPasswordFormProps) {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [passwordMismatch, setPasswordMismatch] = useState(false);

    const validateForm = () => {
        const passwordValidation = validatePassword(newPassword);
        const passwordsMatch = validatePasswordMatch(
            newPassword,
            confirmPassword,
        );

        setValidationErrors(passwordValidation.errors);
        setPasswordMismatch(!passwordsMatch && confirmPassword.length > 0);

        return (
            passwordValidation.isValid &&
            passwordsMatch &&
            newPassword.length > 0 &&
            confirmPassword.length > 0
        );
    };

    const isFormValid = useMemo(() => {
        const passwordValidation = validatePassword(newPassword);
        const passwordsMatch = validatePasswordMatch(
            newPassword,
            confirmPassword,
        );

        return (
            passwordValidation.isValid &&
            passwordsMatch &&
            newPassword.length > 0 &&
            confirmPassword.length > 0
        );
    }, [newPassword, confirmPassword]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (validateForm()) {
            try {
                await onSubmit(newPassword);
            } catch (error) {
                // Error handling is managed by parent component
                console.error("Password submission failed:", error);
            }
        }
    };

    const handlePasswordChange = (value: string) => {
        setNewPassword(value);
        if (value.length > 0) {
            const validation = validatePassword(value);
            setValidationErrors(validation.errors);
        } else {
            setValidationErrors([]);
        }
    };

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value);
        if (value.length > 0) {
            setPasswordMismatch(!validatePasswordMatch(newPassword, value));
        } else {
            setPasswordMismatch(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 4 }}>
                <HeaderPrimary>Set New Password</HeaderPrimary>
                <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
                    Please set a new password to continue. Your password must
                    meet the security requirements below.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) =>
                                handlePasswordChange(e.target.value)
                            }
                            error={validationErrors.length > 0}
                            disabled={loading}
                        />
                        {validationErrors.length > 0 && (
                            <List dense>
                                {validationErrors.map((error, index) => (
                                    <ListItem key={index} sx={{ pl: 0 }}>
                                        <ListItemText
                                            primary={error}
                                            primaryTypographyProps={{
                                                color: "error",
                                                variant: "caption",
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) =>
                                handleConfirmPasswordChange(e.target.value)
                            }
                            error={passwordMismatch}
                            helperText={
                                passwordMismatch ? "Passwords do not match" : ""
                            }
                            disabled={loading}
                        />
                    </Box>

                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={!isFormValid || loading}
                            sx={{
                                bgcolor: "#043c4a",
                                height: "50px",
                                minWidth: "120px",
                                color: "#ffffff",
                                ":hover": {
                                    bgcolor: "#043c4a",
                                },
                                ":disabled": {
                                    bgcolor: "#ccc",
                                },
                            }}
                        >
                            {loading ? "Setting Password..." : "Set Password"}
                        </Button>
                    </Box>
                </form>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" color="textSecondary">
                        Password should have at least:
                    </Typography>
                    <List dense>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemText
                                primary="• Minimum 8 characters"
                                primaryTypographyProps={{
                                    variant: "caption",
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemText
                                primary="• 1 special character"
                                primaryTypographyProps={{
                                    variant: "caption",
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemText
                                primary="• 1 uppercase letter"
                                primaryTypographyProps={{
                                    variant: "caption",
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemText
                                primary="• 1 lowercase letter"
                                primaryTypographyProps={{
                                    variant: "caption",
                                }}
                            />
                        </ListItem>
                        <ListItem sx={{ pl: 0 }}>
                            <ListItemText
                                primary="• 1 number"
                                primaryTypographyProps={{
                                    variant: "caption",
                                }}
                            />
                        </ListItem>
                    </List>
                </Box>
            </Box>
        </Container>
    );
}
